import { useCallback } from 'react';

// Large default is used to represent "unlimited" uploads
export const DEFAULT_MAX_FILES = 100000;

/**
 * Metadata returned from successful media upload
 */
export interface MediaUploadMetadata {
  id: string;
  url: string;
  created_at?: string;
  file_size?: number;
  content_type?: string;
  [key: string]: any; // Allow additional properties
}

/**
 * File information during/after upload
 */
export interface MediaUploadedFile {
  name: string;
  size: number;
  type: string;
  data?: File | Blob;
  [key: string]: any; // Allow additional properties
}

/**
 * Configuration options for useMediaFieldUploader hook.
 */
export interface MediaFieldUploaderConfig {
  allowedFileTypes: string[];
  endpoint: string;
  fieldName: string;
  currentItemCount: number;
  maxItems?: number;
  onAdd?: (metadata: MediaUploadMetadata, uploadedFile?: MediaUploadedFile) => void;
  onError?: (error: Error) => void;
  disabled?: boolean;
  maxFileSize?: number;
  /** Maximum number of concurrent uploads (default: 3) */
  maxConcurrentUploads?: number;
}

export interface MediaFieldUploaderReturn {
  /** Whether the uploader should be shown (not disabled and not at capacity) */
  shouldShowUploader: boolean;
  remainingSlots: number;
  endpoint: string;
  fieldName: string;
  allowedFileTypes: string[];
  maxFileSize?: number;
  maxConcurrentUploads: number;
  handleUploadSuccess: (metadata: MediaUploadMetadata, uploadedFile?: MediaUploadedFile) => void;
  handleUploadError: (error: Error) => void;
}

/**
 * Unified hook for managing media field uploads across all media types.
 * Provides configuration and helper functions for integrating with UniversalMediaUploader.
 *
 * This hook:
 * - Receives the media type's upload settings from its canonical config entry
 * - Calculates remaining upload slots based on field max_length
 * - Provides upload handlers and restrictions
 *
 * @param config - Canonical upload settings and field constraints
 * @returns Upload state, settings, and handler functions
 *
 * @example
 * ```tsx
 * const uploader = useMediaFieldUploader({
 *   allowedFileTypes: ['image/*'],
 *   endpoint: '/api/v2/photos.json',
 *   fieldName: 'photo',
 *   currentItemCount: photos.length,
 *   maxItems: field.max_length,
 *   onAdd: handlePhotoAdd,
 * });
 *
 * if (!uploader.shouldShowUploader) {
 *   return null;
 * }
 *
 * return (
 *   <UniversalMediaUploader
 *     endpoint={uploader.endpoint}
 *     allowedFileTypes={uploader.allowedFileTypes}
 *     maxNumberOfFiles={uploader.remainingSlots}
 *     onUploadSuccess={uploader.handleUploadSuccess}
 *   />
 * );
 * ```
 */
export const useMediaFieldUploader = ({
  allowedFileTypes,
  endpoint,
  fieldName,
  currentItemCount,
  maxItems = DEFAULT_MAX_FILES,
  onAdd,
  onError,
  disabled = false,
  maxFileSize,
  maxConcurrentUploads = 3,
}: MediaFieldUploaderConfig): MediaFieldUploaderReturn => {
  const remainingSlots = Math.max(0, maxItems - currentItemCount);

  const shouldShowUploader = !disabled && remainingSlots > 0;

  const handleUploadSuccess = useCallback(
    (metadata: MediaUploadMetadata, uploadedFile?: MediaUploadedFile) => {
      if (onAdd) {
        onAdd(metadata, uploadedFile);
      }
    },
    [onAdd],
  );

  const handleUploadError = useCallback(
    (error: Error) => {
      console.error('Upload error:', error);
      onError?.(error);
    },
    [onError],
  );

  return {
    shouldShowUploader,
    remainingSlots,
    endpoint,
    fieldName,
    allowedFileTypes,
    maxFileSize,
    maxConcurrentUploads,
    handleUploadSuccess,
    handleUploadError,
  };
};

export default useMediaFieldUploader;
